import React from 'react'
import NextImage from 'next/image'
import NextLink from 'next/link'
import { useRouter } from 'next/router'

import { Avatar } from '@wartek-id/avatar'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { ContactDrawer } from 'app/ProfileRevampPage/components/ContactDrawer'
import { EditPersonalDataDrawer } from 'app/ProfileRevampPage/components/EditPersonalDataDrawer'
import { EditProfileDrawer } from 'app/ProfileRevampPage/components/EditProfileDrawer'
import { ProfileSocialShareDrawer } from 'app/ProfileRevampPage/components/ProfileSocialShareDrawer'
import { SHARE_TEXT } from 'app/ProfileRevampPage/constants'
import { Subjects } from 'app/ProfileRevampPage/components/Subjects'
import { useAuth } from 'utils/auth'
import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import { useSETrackEventEditProfile } from 'app/ProfileRevampPage/hooks/useSETrackEventEditProfile'
import { useShare } from 'app/ProfileRevampPage/hooks/useShare'

const DesktopProfileHeader = () => {
  const { session } = useAuth()
  const { user = null } = session || {}
  const isLoggedIn = Boolean(session) && Boolean(user)
  const router = useRouter()
  const { isCurrentUser, isNarasumber, profileData } = useProfile()
  const { SETrackClickContactButton, SETrackClickShareProfile } =
    useSETrackEvent()
  const {
    SETrackClickEditProfileButton,
    SETrackClickProfileItem,
    SETrackOpenEditProfilePopup,
    SETrackCloseEditProfilePopup,
    SETrackOpenPersonalDataPopup,
    SETrackClosePersonalDataPopup,
  } = useSETrackEventEditProfile()
  const {
    shareUrl,
    socialShareDrawerOpen,
    setSocialShareDrawerOpen,
    onClickShare,
  } = useShare()
  const [contactDrawerOpen, setContactDrawerOpen] =
    React.useState<boolean>(false)
  const [editProfileDrawerOpen, setEditProfileDrawerOpen] =
    React.useState<boolean>(false)
  const [editPersonalDataDrawerOpen, setEditPersonalDataDrawerOpen] =
    React.useState<boolean>(false)

  const bannerImage = Boolean(profileData?.bannerImage)
    ? profileData?.bannerImage
    : '/images/profile-default-header.svg'

  const onClickContact = () => {
    SETrackClickContactButton()

    if (!isLoggedIn) {
      return router.push({
        pathname: '/login',
        query: { from: router.asPath },
      })
    }

    setContactDrawerOpen(true)
  }

  const onClickShareButton = () => {
    onClickShare()
    SETrackClickShareProfile({ pageElement: 'button' })
  }

  const onClickEditProfile = () => {
    setEditProfileDrawerOpen(true)
    SETrackClickEditProfileButton()
  }

  const onEditProfileCloseButtonClicked = () => {
    setEditProfileDrawerOpen(false)
    SETrackCloseEditProfilePopup()
  }

  const onEditPersonalDataCloseButtonClicked = () => {
    setEditPersonalDataDrawerOpen(false)
    SETrackClosePersonalDataPopup()
  }

  React.useEffect(() => {
    if (editProfileDrawerOpen) {
      SETrackOpenEditProfilePopup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProfileDrawerOpen])

  React.useEffect(() => {
    if (editPersonalDataDrawerOpen) {
      SETrackOpenPersonalDataPopup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPersonalDataDrawerOpen])

  return (
    <section className="overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="relative h-[280px] overflow-hidden bg-surface-neutral-subdued">
        {isCurrentUser ? (
          <NextLink href="/profil/edit/cover" passHref>
            <a
              className="block h-full cursor-pointer"
              onClick={() => {
                SETrackClickProfileItem({ pageElement: 'HEADER_PICTURE' })
              }}
            >
              <NextImage
                layout="fill"
                objectFit="cover"
                src={bannerImage}
                alt="Cover Image"
                priority
              />
            </a>
          </NextLink>
        ) : (
          <div className="h-full">
            <NextImage
              layout="fill"
              objectFit="cover"
              src={bannerImage}
              alt="Cover Image"
              priority
            />
          </div>
        )}
      </div>
      <div className="px-8 py-6">
        <div className="flex items-center justify-between gap-6">
          <div className="flex min-w-0 items-center gap-5">
            {isCurrentUser ? (
              <NextLink href="/profil/edit/photo" passHref>
                <a
                  className="block cursor-pointer rounded-full bg-white"
                  onClick={() => {
                    SETrackClickProfileItem({ pageElement: 'PROFILE_PICTURE' })
                  }}
                >
                  <Avatar
                    src={profileData?.image}
                    name={profileData?.name}
                    size="xxl"
                    alt="Avatar"
                    className="!h-[132px] !w-[132px] !shadow-none"
                  />
                </a>
              </NextLink>
            ) : (
              <div className="block rounded-full bg-white">
                <Avatar
                  src={profileData?.image}
                  name={profileData?.name}
                  size="xxl"
                  alt="Avatar"
                  className="!h-[132px] !w-[132px] !shadow-none"
                />
              </div>
            )}
            <div className="min-w-0">
              <Text as="h1" variant="heading-lg" className="break-words">
                {profileData?.name}
              </Text>
              {isNarasumber ? (
                <div className="mt-1 flex items-center space-x-1">
                  <NextImage
                    layout="fixed"
                    src="/icon/star.png"
                    alt="Star"
                    width="16px"
                    height="16px"
                  />
                  <Text variant="body-sm">Narasumber Praktik Baik IKM</Text>
                </div>
              ) : null}
              {Boolean(profileData?.school) ? (
                <Text variant="body-sm" className="break-words">
                  {profileData.school}
                </Text>
              ) : null}
              {Boolean(profileData?.jenisPtk) ? (
                <Text variant="body-sm" className="break-words">
                  {profileData.jenisPtk}
                </Text>
              ) : null}
              {(profileData?.subjects ?? []).length > 0 ? <Subjects /> : null}
            </div>
          </div>
          <div className="flex shrink-0 gap-3">
            {isCurrentUser ? (
              <Button
                color="white"
                className="space-x-2"
                onClick={onClickEditProfile}
              >
                <Text variant="action">Edit profil</Text>
                <Icon fontSize="small" className="-mb-1">
                  arrow_drop_down
                </Icon>
              </Button>
            ) : null}
            {isNarasumber && !isCurrentUser ? (
              <Button color="white" onClick={onClickContact}>
                <Text variant="action">Hubungi guru</Text>
              </Button>
            ) : null}
            <Button
              color="white"
              className="space-x-2"
              onClick={onClickShareButton}
            >
              <Icon fontSize="small" className="-mb-1">
                share
              </Icon>
              <Text variant="action">Bagikan profil</Text>
            </Button>
          </div>
        </div>
      </div>

      {socialShareDrawerOpen ? (
        <ProfileSocialShareDrawer
          isOpen={true}
          onClose={() => setSocialShareDrawerOpen(false)}
          shareUrl={shareUrl}
          shareText={SHARE_TEXT}
        />
      ) : null}
      {contactDrawerOpen ? (
        <ContactDrawer
          isOpen={true}
          onClose={() => setContactDrawerOpen(false)}
          onShare={onClickShare}
        />
      ) : null}
      {editProfileDrawerOpen ? (
        <EditProfileDrawer
          isOpen={true}
          onClose={() => setEditProfileDrawerOpen(false)}
          onEditPersonalDataClicked={() => {
            setEditProfileDrawerOpen(false)
            setEditPersonalDataDrawerOpen(true)
          }}
          onCloseButtonClicked={onEditProfileCloseButtonClicked}
        />
      ) : null}
      {editPersonalDataDrawerOpen ? (
        <EditPersonalDataDrawer
          isOpen={true}
          onClose={() => setEditPersonalDataDrawerOpen(false)}
          onCloseButtonClicked={onEditPersonalDataCloseButtonClicked}
        />
      ) : null}
    </section>
  )
}

export default DesktopProfileHeader
